import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Ban, Trash2, Plus, Mail, User, MapPin, ShieldAlert } from 'lucide-react';

const TYPE_META = {
  email: { icon: Mail, label: 'Email', color: 'bg-blue-100 text-blue-700' },
  name: { icon: User, label: 'Name', color: 'bg-amber-100 text-amber-700' },
  address: { icon: MapPin, label: 'Address', color: 'bg-purple-100 text-purple-700' },
};

const BlocklistPanel = ({ adminFetch, API_URL, active }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState('email');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/blocklist`);
      if (r.ok) setEntries(await r.json());
    } catch (e) { /* noop */ }
    setLoading(false);
  }, [adminFetch, API_URL]);

  useEffect(() => { if (active) fetchEntries(); }, [active, fetchEntries]);

  const addEntry = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setAdding(true);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/blocklist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ block_type: type, value: value.trim(), reason: reason.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).detail);
      toast.success('Blocklist entry added');
      setValue(''); setReason('');
      fetchEntries();
    } catch (err) {
      toast.error(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const removeEntry = async (block_id) => {
    if (!window.confirm('Remove this blocklist entry?')) return;
    try {
      const r = await adminFetch(`${API_URL}/api/admin/blocklist/${block_id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Failed');
      toast.success('Entry removed');
      setEntries((prev) => prev.filter((x) => x.block_id !== block_id));
    } catch (err) {
      toast.error('Failed to remove entry');
    }
  };

  return (
    <div data-testid="blocklist-panel">
      <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-red-50 border border-red-100">
        <ShieldAlert className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-800">
          Blocked emails, names, or addresses can't be used to sign up, log in, or check out.
          Banning a customer adds their details here automatically — you can also add entries manually below.
        </p>
      </div>

      <Card className="border-slate-100 mb-6">
        <CardContent className="p-4">
          <form onSubmit={addEntry} className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="sm:w-40">
              <label className="text-xs font-medium text-slate-600">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
                data-testid="blocklist-type-select"
              >
                <option value="email">Email</option>
                <option value="name">Name</option>
                <option value="address">Address</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600">Value</label>
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={type === 'email' ? 'bad@actor.com' : type === 'name' ? 'John Doe' : '123 Main St, 78701'}
                className="mt-1"
                data-testid="blocklist-value-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs font-medium text-slate-600">Reason (optional)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why?" className="mt-1" />
            </div>
            <Button type="submit" disabled={adding} className="bg-red-600 hover:bg-red-700 text-white" data-testid="blocklist-add-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add
            </Button>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
      ) : entries.length === 0 ? (
        <div className="text-center py-12">
          <Ban className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No blocked accounts or entries yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => {
            const meta = TYPE_META[entry.block_type] || TYPE_META.email;
            const Icon = meta.icon;
            return (
              <Card key={entry.block_id} className="border-slate-100">
                <CardContent className="p-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge className={`${meta.color} text-xs flex items-center gap-1`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </Badge>
                    <span className="font-medium text-slate-800 truncate">{entry.value_display}</span>
                    <Badge variant="outline" className="text-xs">{entry.source === 'ban' ? 'from ban' : 'manual'}</Badge>
                    {entry.reason && <span className="text-xs text-slate-400 truncate hidden sm:inline">— {entry.reason}</span>}
                  </div>
                  <button
                    onClick={() => removeEntry(entry.block_id)}
                    className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 flex-shrink-0"
                    title="Remove entry"
                    data-testid={`blocklist-delete-${entry.block_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlocklistPanel;
